<template>
    <TableFieldSet v-if="configStore.configObject?.store" title="Kontaktdaten">
        <div v-if="!tableStore.selectedEvent" class="col-12 flex flex-column sm:flex-row sm:p-2 sm:pt-0">
            <div class="flex-order-1 sm:flex-order-0">
              <div class="flex align-items-center">
                <p class="mb-2 font-bold">Ihre Reservierung bei {{ configStore.configObject.store.name }}:</p>
                <Button type="button" icon="pi pi-pencil" class="mb-2" text @click="router.back()"/>
              </div>
                <TableIcon icon="pi pi-calendar">
                    {{ setViewDate }}
                </TableIcon>
                <TableIcon icon="pi pi-clock">
                    {{ tableStore.chosenTable.time }}
                </TableIcon>
                <TableIcon icon="pi pi-user">
                    {{ tableStore.chosenTable.persons }} Personen
                </TableIcon>
                <TableIcon icon="pi pi-compass">
                    {{ configStore.address }}
                </TableIcon>
            </div>
          <div v-if="userStore.session === null" class="ml-auto flex-order-0 sm:flex-order-1 mb-3 sm:mb-0">
            <TableButton :label="loginLabel" class="pt-0 pb-0" link type="button" @clicked="toggleLogin" />
          </div>
          <div v-else class="ml-auto flex-order-0 sm:flex-order-1 mb-3 py-0 sm:mb-0">
            <UserMenu />
          </div>
        </div>
        <!-- Event-specific summary and selection -->
        <div v-if="tableStore.selectedEvent" class="col-12 mt-2">
            <!-- Payment not configured error -->
            <div v-if="!isPaymentConfigured" class="p-3 mb-3 border-round" style="background-color: #fee2e2; border: 1px solid #fecaca;">
                <div class="flex align-items-center gap-2">
                    <i class="pi pi-exclamation-triangle" style="color: #dc2626;"></i>
                    <span class="font-bold" style="color: #dc2626;">Buchung derzeit nicht möglich</span>
                </div>
                <p class="text-sm mt-2 mb-0" style="color: #991b1b;">
                    Die Zahlungsdaten für dieses Restaurant sind nicht konfiguriert. 
                    Bitte kontaktieren Sie das Restaurant direkt, um eine Reservierung vorzunehmen.
                </p>
            </div>
            <div class="p-3 border-round surface-0 shadow-2 mb-3 bg-white">
                <h3 class="text-xl font-bold mb-3 pb-2 border-bottom-1 surface-border">Zusammenfassung</h3>

                <div class="grid mb-3">
                    <div class="col-12 md:col-6">
                        <div class="text-500 text-sm mb-1">Event</div>
                        <div class="font-bold">{{ tableStore.selectedEvent.name }}</div>
                        <div v-if="tableStore.selectedEvent.flyer_url" class="mt-1 text-sm">
                            <a :href="tableStore.selectedEvent.flyer_url" target="_blank" rel="noopener">Mehr Informationen</a>
                        </div>
                    </div>
                    <div class="col-12 md:col-6">
                        <div class="text-500 text-sm mb-1">Datum</div>
                        <div class="font-bold">{{ formatEventDateTime(tableStore.selectedEvent.event_date) }}</div>
                    </div>
                </div>

                <div class="mt-4">
                    <!-- Type 1: simple tickets -->
                    <div v-if="tableStore.selectedEvent.event_type === 1">
                        <!-- Sold out message -->
                        <div v-if="ticketOptions.length === 0" class="p-3 mb-3 border-round" style="background-color: #fee2e2; border: 1px solid #fecaca;">
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-exclamation-triangle" style="color: #dc2626;"></i>
                                <span class="font-bold" style="color: #dc2626;">Dieses Event ist leider ausgebucht.</span>
                            </div>
                            <p class="text-sm mt-2 mb-0" style="color: #991b1b;">Es sind keine Tickets mehr verfügbar.</p>
                        </div>
                        <!-- Ticket selection when available -->
                        <template v-else>
                            <div class="field mb-3">
                                <label class="block text-500 text-sm mb-1">Anzahl Tickets</label>
                                <TableDropDown id="ticketCount"
                                               :all-options="ticketOptions"
                                               name="ticketCount"
                                               placeholder="Anzahl wählen"
                                               :value="selectedTicketCount"
                                               :error-message="''"
                                               @update:modelValue="(v: any) => selectedTicketCount = v" />
                                <div class="text-xs text-500 mt-1" v-if="tableStore.selectedEvent.available_seats !== undefined">
                                    Noch {{ tableStore.selectedEvent.available_seats }} Tickets verfügbar
                                </div>
                            </div>

                            <div class="text-500 text-sm mb-2">Kostenaufstellung</div>
                            <div class="flex justify-content-between align-items-center mb-2 p-2 surface-50 border-round">
                                <div>
                                    <span class="font-bold mr-2">{{ selectedTicketCount || 0 }}x</span>
                                    <span>Ticket(s)</span>
                                    <div class="text-xs text-500 mt-1" v-if="selectedTicketCount > 0">
                                        Einzelpreis: {{ formatCurrency(tableStore.selectedEvent.ticket_price) }}
                                    </div>
                                </div>
                                <div class="font-bold">
                                    {{ formatCurrency(totalTicketPrice) }}
                                </div>
                            </div>
                        </template>
                    </div>
                    <!-- Type 2: table options -->
                    <div v-else>
                        <!-- Sold out message when all options are unavailable -->
                        <div v-if="allSeatOptionsSoldOut" class="p-3 mb-3 border-round" style="background-color: #fee2e2; border: 1px solid #fecaca;">
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-exclamation-triangle" style="color: #dc2626;"></i>
                                <span class="font-bold" style="color: #dc2626;">Dieses Event ist leider ausgebucht.</span>
                            </div>
                            <p class="text-sm mt-2 mb-0" style="color: #991b1b;">Es sind keine Tische mehr verfügbar.</p>
                        </div>
                        <!-- Table selection when available -->
                        <template v-else>
                            <div class="field mb-3">
                                <label class="block text-500 text-sm mb-1">Sitzplatz-Kombination</label>
                                <TableDropDown id="seatOption"
                                               :all-options="seatOptions"
                                               name="seatOption"
                                               placeholder="Option wählen"
                                               :value="selectedSeatOptionId"
                                               :error-message="''"
                                               @update:modelValue="(v: any) => selectedSeatOptionId = v" />
                            </div>

                            <div class="text-500 text-sm mb-2">Kostenaufstellung</div>
                            <div class="flex justify-content-between align-items-center mb-2 p-2 surface-50 border-round" v-if="selectedSeatOptionId">
                                <div v-if="getSelectedSeatOption(selectedSeatOptionId)">
                                    <span class="font-bold mr-2">1x</span>
                                    <span>{{ getSelectedSeatOption(selectedSeatOptionId).seats }} Plätze</span>
                                </div>
                                <div class="font-bold">
                                    {{ formatCurrency(selectedSeatPrice) }}
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="flex justify-content-between align-items-center mt-3 pt-3 border-top-1 surface-border">
                        <div class="text-lg font-bold">Gesamtsumme</div>
                        <div class="text-xl font-bold text-primary">
                            {{ formatCurrency(tableStore.selectedEvent.event_type === 1 ? totalTicketPrice : selectedSeatPrice) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Only show reservation form if payment is configured (for events) or if it's a regular table reservation -->
        <template v-if="isPaymentConfigured">
            <div class="col-12 mt-2">
                <Divider/>
            </div>
            <transition mode="out-in" name="slide-fade">
                <div v-if="login || login && userStore.session === null" class="col-12 mt-2">
                    <TableLogin @loggedIn="toggleLogin"/>
                </div>
                <div v-else class="col-12 mt-2">
                    <TableReservation />
                </div>
            </transition>
        </template>
    </TableFieldSet>
</template>

<script lang="ts" setup>
import TableFieldSet from "@/components/TableFieldSet.vue";
import Button from "primevue/button";
import TableLogin from "@/components/TableLogin.vue";
import TableButton from "@/components/TableButton.vue";
import TableReservation from "@/components/TableReservation.vue";
import Divider from 'primevue/divider';
import {useConfigStore} from "@/stores/ConfigStore";
import {useTableStore} from "@/stores/TableStore";
import {computed, onMounted, ref, watch} from "vue";
import TableIcon from "@/components/TableIcon.vue";
import {useUserStore} from "@/stores/UserStore";
import UserMenu from "@/components/User/UserMenu.vue";
import {useRoute} from "vue-router";
import router from "@/router";
import TableDropDown from "@/components/TableDropDown.vue";

const configStore = useConfigStore();
const tableStore = useTableStore();
const userStore = useUserStore();
const route = useRoute();

// Event-related selections for the summary box are stored in Pinia to be available in the submit step
// Use store-backed refs for two-way binding
const selectedTicketCount = computed({
  get: () => (tableStore as any).selectedTicketCount,
  set: (v) => ((tableStore as any).selectedTicketCount = v as any),
});
const selectedSeatOptionId = computed({
  get: () => (tableStore as any).selectedSeatOptionId,
  set: (v) => ((tableStore as any).selectedSeatOptionId = v as any),
});

const login = ref(false);
const loginLabel = ref('Einloggen');

// Check if payment is configured for event reservations
const isPaymentConfigured = computed(() => {
  const ev: any = tableStore.selectedEvent;
  if (!ev) return true; // Not an event reservation, no payment check needed
  return ev.payment_configured === true;
});

onMounted(async () => {
  if(!configStore.configObject?.store) {
    router.push({
      name: 'bookingDetails',
      query: {
        store: route.query.store,
      },
    })
  }
})

// Load options for type 2 when an event is selected
watch(() => tableStore.selectedEvent, async (ev) => {
  if (ev && ev.event_type === 2) {
    await tableStore.loadEventOptions(ev.id ?? ev.eventid);
  }
}, { immediate: true });

// Ticket dropdown options (type 1) - limited by available seats
const ticketOptions = computed(() => {
  const ev: any = tableStore.selectedEvent;
  if (!ev) return [] as any[];
  // Use available_seats if provided, otherwise fall back to total_tickets
  const availableSeats = ev.available_seats !== undefined ? ev.available_seats : ev.total_tickets;
  if (availableSeats <= 0) return [] as any[]; // No tickets available
  const max = Math.min(availableSeats, 10); // Cap at 10 for UI
  const arr: any[] = [];
  for (let i = 1; i <= max; i++) {
    arr.push({ label: i === 1 ? '1 Ticket' : `${i} Tickets`, amount: String(i) });
  }
  return arr;
});

// Preselect ticket count from persons chosen in previous step
watch(ticketOptions, (opts) => {
  if (!opts || opts.length === 0) return;
  const persons = tableStore.chosenTable?.persons;
  if (persons) {
    const val = String(persons);
    if (opts.some((o: any) => String(o.amount) === val)) {
      selectedTicketCount.value = val as any;
      return;
    }
  }
  // fallback: first option if nothing else set
  if (!selectedTicketCount.value) {
    selectedTicketCount.value = opts[0].amount as any;
  }
}, { immediate: true });
const totalTicketPrice = computed(() => {
  const ev: any = tableStore.selectedEvent;
  const count = Number(selectedTicketCount.value || 0);
  const price = Number(ev?.ticket_price || 0);
  return count * price;
});

// Seat options (type 2) - show availability and disable sold-out options
const seatOptions = computed(() => {
  return (tableStore.eventOptions || []).map((o: any) => {
    const available = o.available !== undefined ? o.available : o.quantity;
    const isAvailable = available > 0;
    return {
      label: `${o.seats} Plätze (${available} verfügbar) – ${formatCurrency(o.price)}${!isAvailable ? ' - AUSGEBUCHT' : ''}`,
      amount: String(o.id),
      disabled: !isAvailable
    };
  });
});

// Check if all seat options are sold out (type 2)
const allSeatOptionsSoldOut = computed(() => {
  const options = seatOptions.value;
  if (!options || options.length === 0) return false; // No options loaded yet
  return options.every((o: any) => o.disabled === true);
});
const selectedSeatPrice = computed(() => {
  const id = Number(selectedSeatOptionId.value || 0);
  const found = (tableStore.eventOptions || []).find((o: any) => Number(o.id) === id);
  return found ? Number(found.price) : 0;
});

function getSelectedSeatOption(id: any) {
  if (!tableStore.eventOptions) return null;
  return (tableStore.eventOptions as any[]).find((o: any) => String(o.id) === String(id));
}

function formatEventTime(unix?: number | null) {
  if (!unix) return '-';
  const d = new Date(unix * 1000);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
function formatEventDateTime(unix?: number | null) {
  if (!unix) return '-';
  const d = new Date(unix * 1000);
  const weekday = d.toLocaleDateString('de-DE', { weekday: 'long' });
  const dayMonth = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${weekday} ${dayMonth} ${time}`;
}
function formatCurrency(v: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);
}

function toggleLogin() {
    login.value = !login.value;
    login.value ? loginLabel.value = 'Zurück' : loginLabel.value = 'Einloggen';
}

const setViewDate = computed(() => {
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return (tableStore.chosenTable.date as any).toLocaleString("de-DE", options);
});
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateX(20px);
    opacity: 0;
}
</style>